import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { handleApiError } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker } from '~/components/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import dayjs from 'dayjs';
import { handleGeneratePDF } from '~/thunks/estimate/estimateThunk';
import { IResponseGetFile } from '~/utils/interface/file';
import { handleGenerateSignDocument } from '~/thunks/file/fileThunk';
import esignApi from '~/apis/esign';
import ModalAlertConfirm from '~/components/common/modal-alert-confirm/ModalAlertConfirm';

type SendDocumentDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, success?: boolean) => void;
  id?: number;
  documentFile?: IResponseGetFile | undefined;
  jobId?: string;
  customerEmail?: string;
};

const initialValues = {
  to: [],
  cc: [],
  expirationDate: '',
  sendWithDefaultEmail: false,
  defaultEmail: '',
};

const SendDocumentEditNoteDialog = (props: SendDocumentDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/job', 'admin/esign']);
  useAppSelector((state) => state.customer);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [documentFile, setDocumentFile] = useState<IResponseGetFile>();
  const [toEmail, setToEmail] = useState<string>();
  const [chipToEmails, setChipToEmails] = useState<Array<string | never[]>>([]);
  const [ccEmail, setCcEmail] = useState<string>();
  const [chipCcEmails, setChipCcEmails] = useState<Array<string | never[]>>([]);
  const [loading, setLoading] = useState(true);
  const [embedUrl, setEmbedUrl] = useState<string>('');
  const [openSubmitConfirm, setOpenSubmitConfirm] = useState<boolean>(false);
  const [openSubmitConfirmDescription, setOpenSubmitConfirmDescription] = useState<string>('');
  const [defaultEmail, setDefaultEmail] = useState<string>('');
  const handleCancel = () => {
    handleSetOpen(false);
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        const response = await esignApi.generateSignEmbedUrl({
          ...values,
          id: documentFile?.id,
          jobId: props.jobId,
        });

        // When user email is not verified on Bold Sign
        if (response.data.data.error === 'VERIFY_EMAIL_NEEDED') {
          setOpenSubmitConfirmDescription(
            t('openSubmitConfirm.description', { ns: 'admin/esign', defaultEmail: response.data.data.defaultEmail })
          );
          setDefaultEmail(response.data.data.defaultEmail ?? '');
          setOpenSubmitConfirm(true);
          return false;
        }
        if (response.data.data.embedUrl) setEmbedUrl(response.data.data.embedUrl);
      } catch (e: unknown) {
        setDisableButton(false);
        handleApiError(e, setFieldError);
      } finally {
        setDisableButton(false);
      }
      return;
    },
  });

  useEffect(() => {
    if (props.customerEmail) {
      setChipToEmails([props.customerEmail]);
    }
  }, [props.customerEmail]);
  useEffect(() => {
    if (toEmail) form.setFieldValue('to', [...chipToEmails, toEmail]);
    else form.setFieldValue('to', chipToEmails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toEmail, chipToEmails, props.open]);

  useEffect(() => {
    if (ccEmail) form.setFieldValue('cc', [...chipCcEmails, ccEmail]);
    else form.setFieldValue('cc', chipCcEmails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccEmail, chipCcEmails]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    setDisableButton(false);
    form.resetForm();
    form.setFieldValue('to', []);
    form.setFieldValue('cc', []);
    setEmbedUrl('');

    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  useEffect(() => {
    const generatPDF = async () => {
      try {
        setLoading(true);
        if (props.id) {
          const result = await dispatch(handleGeneratePDF(props.id.toString())).unwrap();
          result && setDocumentFile(result);
        } else if (props.documentFile) {
          const result = await dispatch(
            handleGenerateSignDocument({ id: props.documentFile.id, jobId: props.jobId })
          ).unwrap();
          setDocumentFile(result);
        }
      } catch (e: unknown) {
        handleApiError(e);
      } finally {
        setLoading(false);
      }
    };
    props.open && generatPDF();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, props.id, props.open]);
  //#endregion Handle Function

  return (
    <Box>
      {embedUrl ? (
        <Dialog
          open={props.open}
          onClose={() => handleSetOpen(false)}
          fullWidth
          maxWidth='xl'
          PaperProps={{
            sx: {
              height: '100%',
              width: '100%',
            },
          }}
        >
          <DialogTitle>
            <IconButton onClick={() => handleSetOpen(false, true)} className='absolute right-[10px] top-[10px]'>
              <CloseIcon className='w-[10px] h-[10px]' />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <iframe src={embedUrl} title='esign-review-iframe' width='100%' height='100%'></iframe>
          </DialogContent>
        </Dialog>
      ) : (
        <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='lg'>
          <DialogTitle>
            {t('estimate.esign.sendDocument')}
            <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
              <CloseIcon className='w-[10px] h-[10px]' />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box className='flex'>
              <Box className='w-2/5 pr-2'>
                <Box className='w-full mb-3'>
                  <InputLabel>{t('estimate.esign.to')}</InputLabel>
                  <Autocomplete
                    multiple
                    id='tags-filled'
                    options={[]}
                    defaultValue={props.customerEmail ? [props.customerEmail] : []}
                    freeSolo
                    onChange={(event, newValue) => {
                      setToEmail('');
                      newValue && setChipToEmails(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          style: {
                            paddingTop: 0,
                            paddingBottom: 0,
                            height: 'fit-content',
                          },
                        }}
                        name='to'
                        placeholder={t('estimate.esign.to')}
                        error={Boolean(form.errors.to)}
                        helperText={form.errors.to}
                        onChange={(e: any) => {
                          setToEmail(e.target.value);
                        }}
                      />
                    )}
                  />
                </Box>

                <Box className='w-full mb-3'>
                  <InputLabel>{t('estimate.esign.cc')}</InputLabel>
                  <Autocomplete
                    multiple
                    id='tags-filled'
                    options={[]}
                    defaultValue={[]}
                    className='height-fit'
                    freeSolo
                    onChange={(event, newValue) => {
                      setCcEmail('');
                      newValue && setChipCcEmails(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          style: {
                            paddingTop: 0,
                            paddingBottom: 0,
                            height: 'fit-content',
                          },
                        }}
                        name='to'
                        placeholder={t('estimate.esign.cc')}
                        onChange={(e: any) => setCcEmail(e.target.value)}
                        error={Boolean(form.errors.cc)}
                        helperText={form.errors.cc}
                      />
                    )}
                  />
                </Box>
                <Box className='w-full mb-3'>
                  <InputLabel>{t('estimate.esign.expirationDate')}</InputLabel>
                  <BaseDatePicker
                    value={form.values.expirationDate}
                    onChange={(date) => {
                      if (date) {
                        const selectedDate = dayjs(date).format('YYYY-MM-DD');
                        form.setFieldValue('expirationDate', selectedDate);
                      }
                    }}
                    error={Boolean(form.errors.expirationDate)}
                    helperText={t('estimate.esign.expirationDateError')}
                  />
                </Box>
              </Box>
              <Box className='w-3/5'>
                {loading ? (
                  <Box display='flex' justifyContent='center' alignItems='center' width='100%' height='600px'>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <iframe src={documentFile && documentFile.url} title='pdf-iframe' width='100%' height='600'></iframe>
                )}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions className='justify-end'>
            <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
              {t('cancel', { ns: 'admin/common' })}
            </Button>
            <Button
              color='primary'
              variant='contained'
              disabled={disableButton || loading}
              onClick={() => {
                setDisableButton(true);
                form.handleSubmit();
              }}
            >
              {t('estimate.esign.reviewSigning')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <ModalAlertConfirm
        open={openSubmitConfirm}
        description={openSubmitConfirmDescription}
        confirm={t('openSubmitConfirm.confirm', { ns: 'admin/esign' })}
        onCancel={() => setOpenSubmitConfirm(false)}
        onConfirm={() => {
          setOpenSubmitConfirm(false);
          form.setFieldValue('sendWithDefaultEmail', true);
          form.setFieldValue('defaultEmail', defaultEmail);
          form.handleSubmit();
        }}
      />
    </Box>
  );
};

export default memo(SendDocumentEditNoteDialog);
